/**
 * ADAPTER: konfiguracja silnika 1/2 (`placements`) → graf sceny.
 *
 * Jeden renderer dla wszystkiego (OWNER 2026-10-08, pkt 22: „nie utrzymuj
 * dwóch równoległych rendererów"). Stary format konfiguracji nadal jest
 * PRZYJMOWANY (kontrakt v1 przekazany backendowi), ale rysuje go ten sam
 * renderer warstw co schemat 3: każde stare miejsce to po prostu warstwa
 * w odpowiednim regionie, z geometrią, jaką miało w silniku 2, a każdy
 * preset — efekt z ogólnej biblioteki.
 */

import { Dekoracja, OGRANICZENIA } from './kontrakt';

/** Parametry presetu z wartościami domyślnymi (walidator już je przyciął). */
function parametry(d: Dekoracja): { speed: number; delay: number; density: number; opacity: number; scale: number; rotation: number } {
  const p = d.parametry;
  return {
    speed: p.speed ?? 1, delay: p.delay ?? 0, density: p.density ?? 8,
    opacity: p.opacity ?? 1, scale: p.scale ?? 1, rotation: p.rotation ?? 0,
  };
}
import {
  Dlugosc, Efekt, Region, RodzajEfektu, Scena, ScenaRegionu, Warstwa, WarstwaBaza, WarstwaCzastki, WarstwaKsztalt,
  WarstwaObraz,
} from './scena.model';

const px = (v: number): Dlugosc => ({ v, u: 'px' });
const pr = (v: number): Dlugosc => ({ v, u: '%' });

function efektZPresetu(d: Dekoracja, os: readonly [number, number]): Efekt[] {
  const p = parametry(d);
  const mnoznik = 1 / p.speed;
  const baza = (typ: RodzajEfektu, czas: number): Efekt => ({
    typ, czas: czas * mnoznik, opoznienie: p.delay * 1000, przerwa: 0, powtorzenia: Infinity, faza: 0,
    easing: 'ease-in-out', amplituda: 6, dystans: 104, kierunek: 90, obrot: 0, krycie: [0.45, 1], skala: [1, 1.08],
    os, naprzemiennie: false, kolor: null, promien: 8, ile: 4, klatki: [], sciezka: [], obracajWzdluz: false,
  });
  switch (d.preset) {
    case 'FLOAT': return [baza('FLOAT', 3000)];
    case 'SWAY': return [{ ...baza('SWAY', 4000), amplituda: 4 }];
    case 'PULSE': return [baza('PULSE', 2000)];
    case 'FADE': return [baza('FADE', 4000)];
    case 'BOUNCE_SUBTLE': return [{ ...baza('BOUNCE', 2000), amplituda: 4 }];
    case 'SHIMMER': return [{ ...baza('SHIMMER', 5000) }];
    case 'GLOW': return [{ ...baza('GLOW', 3000), promien: 8 }];
    default: return [];
  }
}

function warstwaBazowa(d: Dekoracja, region: Region, nad: Partial<WarstwaBaza>): Omit<WarstwaBaza, 'typ'> {
  const os = (nad.poczatek ?? [0.5, 0.5]) as readonly [number, number];
  return {
    id: d.miejsce.toLowerCase(), region, tryb: 'ABSOLUTE', x: px(0), y: px(0), dx: 0, dy: 0, szer: null, wys: null,
    maxSzer: OGRANICZENIA[d.miejsce].maxSzerokosc, maxWys: OGRANICZENIA[d.miejsce].maxWysokosc,
    poczatek: [0, 0], lustro: 'NONE', obrot: parametry(d).rotation, skala: parametry(d).scale, krycie: parametry(d).opacity,
    z: 0, efekty: efektZPresetu(d, d.preset === 'SWAY' ? [0.5, 0] : os), ruchOgraniczony: 'FIRST_FRAME',
    urlStatyczny: d.zasob.urlStatyczny ?? null, minEkran: null, maxEkran: null,
    ...nad,
  };
}

function obraz(d: Dekoracja, region: Region, nad: Partial<WarstwaObraz> = {}): WarstwaObraz {
  const animowany = d.zasob.rodzaj === 'LIGHTWEIGHT_VECTOR_ANIMATION' || d.zasob.rodzaj === 'ANIMATED_IMAGE';
  const statyczny = d.zasob.urlStatyczny ?? null;
  return {
    ...warstwaBazowa(d, region, nad),
    typ: 'IMAGE',
    url: d.zasob.url,
    dopasowanie: 'CONTAIN',
    animowanyPlik: animowany,
    epizod: animowany && statyczny ? [6000, 18000] : null,
    ...nad,
    ruchOgraniczony: animowany ? (statyczny ? 'STATIC_ASSET' : 'HIDE') : 'FIRST_FRAME',
  } as WarstwaObraz;
}

/** Region, w którym silnik 1/2 rysował dane miejsce. */
const REGION_MIEJSCA: Readonly<Record<string, Region>> = {
  HEADER_BACKDROP: 'APP_HEADER', HEADER_DECORATION: 'APP_HEADER', LOGO_BADGE: 'LOGO', LOGO_SIDE: 'LOGO',
  SECTION_ACCENT: 'SECTION_HEADER', SECTION_ACCENT_RIGHT: 'SECTION_HEADER', EVENT_HERO: 'EVENT_HERO',
  SEASONAL_BANNER: 'EVENT_HERO', HOME_TOP_DECORATION: 'EVENT_HERO', MENU_DECORATION: 'POST_CONTENT',
  FOOTER_SCENE: 'FOOTER', CONTENT_BACKDROP: 'CONTENT_BACKGROUND', EMPTY_STATE_DECORATION: 'EMPTY_STATE',
  HOME_BACKGROUND_OVERLAY: 'FULL_SCREEN_OVERLAY', BOTTOM_NAV_DECORATION: 'BOTTOM_NAV',
};

/** Warstwa z dekoracji silnika 1/2 — geometria jak w silniku 2. */
function warstwaZDekoracji(d: Dekoracja): { region: Region; w: Warstwa; wysokosc?: number } | null {
  // OPAD (SNOW / FALLING) — w każdym miejscu, jak w silniku 1: kilka kopii
  // obrazka spadających w regionie tego miejsca (liczba ≤ sufit miejsca).
  if ((d.preset === 'SNOW' || d.preset === 'FALLING') && d.zasob.rodzaj !== 'CSS_PRESET_DECORATION') {
    const p = parametry(d);
    const region = REGION_MIEJSCA[d.miejsce] ?? 'FULL_SCREEN_OVERLAY';
    const limit = OGRANICZENIA[d.miejsce];
    const czastki: WarstwaCzastki = {
      ...warstwaBazowa(d, region, { tryb: region === 'FULL_SCREEN_OVERLAY' ? 'FIXED' : 'ABSOLUTE', efekty: [] }),
      typ: 'PARTICLES', url: d.zasob.url,
      ile: Math.max(1, Math.min(Math.round(p.density), limit.maxElementow)),
      obszar: [0.04, -0.03, 0.92, 0.01], kierunek: 90,
      predkosc: [7 * p.speed, 12 * p.speed], droga: 104,
      obrotCzastki: d.preset === 'SNOW' ? [80, 160] : [0, 0],
      skalaCzastki: [0.55 * p.scale, 1.1 * p.scale], kryciePrzedzial: [p.opacity, p.opacity],
      zycie: [9000 / p.speed, 15000 / p.speed], opoznienia: [0, 15000 / p.speed],
      dryf: d.preset === 'SNOW' ? 8 : 0, rozmiar: Math.min(64, limit.maxSzerokosc),
    };
    const wPrzeplywie = region === 'EVENT_HERO' || region === 'POST_CONTENT' || region === 'PRE_RESTAURANTS';
    return { region, w: czastki, wysokosc: wPrzeplywie ? limit.maxWysokosc : undefined };
  }
  if (d.zasob.rodzaj === 'CSS_PRESET_DECORATION') {
    const k: WarstwaKsztalt = {
      ...warstwaBazowa(d, 'CONTENT_FOREGROUND', { x: pr(100), poczatek: [1, 0], szer: px(OGRANICZENIA[d.miejsce].maxSzerokosc), wys: px(OGRANICZENIA[d.miejsce].maxWysokosc) }),
      typ: 'SHAPE', ksztalt: 'ELLIPSE', zaokraglenie: 0, obrys: null,
      wypelnienie: { typ: 'SOLID', kolor: 'rgba(250,188,0,0.35)', kat: 180, przystanki: [], url: null, dopasowanie: 'COVER', pozycja: [0.5, 0.5], krycie: 1, mieszanie: 'normal' },
    };
    return { region: 'CONTENT_FOREGROUND', w: k };
  }
  switch (d.miejsce) {
    case 'HEADER_BACKDROP':
      return { region: 'APP_HEADER', w: obraz(d, 'APP_HEADER', { szer: pr(100), wys: pr(100), dopasowanie: 'FILL', maxSzer: null, maxWys: null, z: -1 }) };
    case 'HEADER_DECORATION':
      return { region: 'APP_HEADER', w: obraz(d, 'APP_HEADER', { x: pr(100), poczatek: [1, 0], dopasowanie: 'NONE', z: -1 }) };
    case 'LOGO_BADGE':
      return { region: 'LOGO', w: obraz(d, 'LOGO', { x: px(10.5), y: px(1.5), poczatek: [0.5, 1], dopasowanie: 'NONE', efekty: efektZPresetu(d, [0.5, 1]) }) };
    case 'LOGO_SIDE':
      return { region: 'LOGO', w: obraz(d, 'LOGO', { x: pr(100), dx: 4, y: pr(50), poczatek: [0, 0.5], dopasowanie: 'NONE' }) };
    case 'SECTION_ACCENT':
      return { region: 'SECTION_HEADER', w: obraz(d, 'SECTION_HEADER', { x: px(-6), y: px(30), poczatek: [0, 0.5], dopasowanie: 'NONE' }) };
    case 'SECTION_ACCENT_RIGHT':
      return { region: 'SECTION_HEADER', w: obraz(d, 'SECTION_HEADER', { x: pr(100), y: px(30), poczatek: [1, 0.5], dopasowanie: 'NONE' }) };
    case 'EVENT_HERO':
      return { region: 'EVENT_HERO', w: obraz(d, 'EVENT_HERO', { tryb: 'FLOW', szer: pr(100), dopasowanie: 'COVER', maxSzer: null }) };
    case 'SEASONAL_BANNER':
      return { region: 'EVENT_HERO', w: obraz(d, 'EVENT_HERO', { tryb: 'FLOW', szer: pr(100), wys: px(132), dopasowanie: 'CONTAIN', maxSzer: null }) };
    case 'HOME_TOP_DECORATION':
      return { region: 'EVENT_HERO', w: obraz(d, 'EVENT_HERO', { tryb: 'FLOW', szer: pr(100), wys: px(120), dopasowanie: 'CONTAIN', maxSzer: null }) };
    case 'MENU_DECORATION':
      return { region: 'POST_CONTENT', w: obraz(d, 'POST_CONTENT', { tryb: 'FLOW', szer: pr(100), wys: px(96), dopasowanie: 'CONTAIN', maxSzer: null }) };
    case 'FOOTER_SCENE':
      return { region: 'FOOTER', w: obraz(d, 'FOOTER', { x: pr(100), y: pr(100), poczatek: [1, 1], szer: pr(100), dopasowanie: 'CONTAIN', maxSzer: null }) };
    case 'CONTENT_BACKDROP':
      return { region: 'CONTENT_BACKGROUND', w: obraz(d, 'CONTENT_BACKGROUND', { szer: pr(100), wys: pr(100), dopasowanie: 'FILL', maxSzer: null, maxWys: null }), wysokosc: OGRANICZENIA.CONTENT_BACKDROP.maxWysokosc };
    case 'EMPTY_STATE_DECORATION':
      return { region: 'EMPTY_STATE', w: obraz(d, 'EMPTY_STATE', { x: pr(50), poczatek: [0.5, 0], dopasowanie: 'NONE' }) };
    case 'HOME_BACKGROUND_OVERLAY': {
      if (d.preset === 'SNOW' || d.preset === 'FALLING') {
        const p = parametry(d);
        const czastki: WarstwaCzastki = {
          ...warstwaBazowa(d, 'FULL_SCREEN_OVERLAY', { tryb: 'FIXED', efekty: [] }),
          typ: 'PARTICLES', url: d.zasob.url,
          ile: Math.max(1, Math.min(Math.round(p.density), OGRANICZENIA.HOME_BACKGROUND_OVERLAY.maxElementow)),
          obszar: [0.04, -0.03, 0.92, 0.01], kierunek: 90,
          predkosc: [7 * p.speed, 12 * p.speed], droga: 104,
          obrotCzastki: d.preset === 'SNOW' ? [80, 160] : [0, 0],
          skalaCzastki: [0.55 * p.scale, 1.1 * p.scale], kryciePrzedzial: [p.opacity, p.opacity],
          zycie: [9000 / p.speed, 15000 / p.speed], opoznienia: [0, 15000 / p.speed],
          dryf: d.preset === 'SNOW' ? 8 : 0, rozmiar: OGRANICZENIA.HOME_BACKGROUND_OVERLAY.maxSzerokosc,
        };
        return { region: 'FULL_SCREEN_OVERLAY', w: czastki };
      }
      return { region: 'FULL_SCREEN_OVERLAY', w: obraz(d, 'FULL_SCREEN_OVERLAY', { tryb: 'FIXED', x: pr(100), poczatek: [1, 0], dopasowanie: 'NONE' }) };
    }
    case 'BOTTOM_NAV_DECORATION':
      return { region: 'BOTTOM_NAV', w: obraz(d, 'BOTTOM_NAV', { x: pr(50), poczatek: [0.5, 0], dopasowanie: 'NONE' }) };
    default:
      return null;
  }
}

/** Scena z dekoracji silnika 1/2. */
export function scenaZDekoracji(dekoracje: readonly Dekoracja[]): Scena {
  const regiony: Partial<Record<Region, ScenaRegionu>> = {};
  const adresy = new Set<string>();
  for (const d of dekoracje) {
    const r = warstwaZDekoracji(d);
    if (!r) continue;
    const s = regiony[r.region] ?? {
      region: r.region, tlo: null, wysokosc: null, proporcja: null, maxWysokosc: r.region === 'EVENT_HERO' ? OGRANICZENIA.EVENT_HERO.maxWysokosc : null,
      przytnij: !['LOGO', 'SECTION_HEADER', 'APP_HEADER'].includes(r.region) || r.w.region !== 'APP_HEADER', warstwy: [],
    };
    regiony[r.region] = { ...s, wysokosc: r.wysokosc ?? s.wysokosc, przytnij: !['LOGO', 'SECTION_HEADER'].includes(r.region), warstwy: [...s.warstwy, r.w] };
    if (r.w.typ === 'IMAGE' || r.w.typ === 'PARTICLES') adresy.add(r.w.url);
    if (r.w.urlStatyczny) adresy.add(r.w.urlStatyczny);
  }
  return { regiony, adresy: [...adresy] };
}
